import classNames from 'classnames'
import {
  ChangeEvent,
  KeyboardEvent,
  SyntheticEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

import styles from './SecretInputs.module.scss'

interface SecretInputsProps {
  value: string
  mask: number[] // e.g. [2,4,4] -> "my name john"
  setValue: (value: string | ((prev: string) => string)) => void
  onComplete: (value: string) => void
  onWrapperClick?: () => void
  className?: string
  focus?: boolean
  keyBoardType?: 'text' | 'numeric'
  disabled?: boolean
}

export const SecretInputs = ({
  value,
  setValue,
  mask,
  onComplete,
  keyBoardType = 'text',
  className,
  focus = false,
  disabled = false,
  onWrapperClick
}: SecretInputsProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const totalLength = useMemo(
    () => mask.reduce((sum, n) => sum + Math.max(0, n), 0),
    [mask]
  )

  useLayoutEffect(() => {
    if (!focus) return
    inputRef.current?.focus()
  }, [focus])

  const normalize = useCallback(
    (s: string) => s.replace(/\s+/g, '').slice(0, totalLength),
    [totalLength]
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const next = keyBoardType === 'numeric' ? e.target.value : normalize(e.target.value)
      if (keyBoardType === 'numeric' && isNaN(Number(next))) {
        return
      }
      setValue(next)
  
      if (mask.length > 0 && next.length === totalLength) {
        onComplete(next)
      }
    },
    [normalize, onComplete, setValue, totalLength, mask.length]
  )



  const prevent = useCallback((e: SyntheticEvent) => {
    e.preventDefault()
  }, [])

  const rows = useMemo(() => {
    if (mask.length === 0) {
      return [value.split('')]
    }
  
    let cursor = 0
    return mask.map((len) =>
      Array.from({ length: Math.max(0, len) }).map(() => {
        const char = value[cursor] ?? ''
        cursor += 1
        return char
      })
    )
  }, [mask, value])
  const handleSlotClick = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <div className={classNames(styles.wrapper, className)} onClick={onWrapperClick}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        maxLength={mask.length ? totalLength : undefined}
        inputMode={keyBoardType}
        className={styles.hiddenInput}
        disabled={disabled}
        onChange={handleChange}
        // onKeyDown={handleKeyDown}
        onPaste={prevent}
        onDrop={prevent}
      />

      <div className={styles.rows} onClick={handleSlotClick}>
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className={styles.slots}>
            {row.map((char, idx) => (
                char ? (
                  <span key={idx} className={classNames(styles.char, styles.filled)}>
                    {char}
                  </span>
                ) : (
                  <span
                    key={idx}
                    className={classNames(styles.placeholder, styles.empty)}
                  >
                    *
                  </span>
                )
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}